import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'primereact/dialog'
import { InputTextarea } from 'primereact/inputtextarea'
import { Calendar } from 'primereact/calendar'
import { Checkbox } from 'primereact/checkbox'
import Button from '../../../components/ui/Button'
import FileUpload from '../../../components/ui/FileUpload'
import { DOCUMENT_ACCEPT } from '../../../lib/uploadAccept'
import { parseISODate, toISODate } from '../../../lib/datetime'
import ActionPeopleEditor from './ActionPeopleEditor'
import {
  minutePersonToInput,
  personInputToMinutePerson,
  type MinutePerson,
  type PersonInput,
  type SubActionCreateRequest,
} from '../types'

interface Props {
  visible: boolean
  /** Termin alt sınırı (üst aksiyonun toplantı tarihi) — varsa. */
  minDeadline?: Date
  /** Termin üst sınırı = üst aksiyonun termini (066 — alt aksiyon termini ondan ileri olamaz). */
  maxDeadline?: Date
  saving: boolean
  /** Ekler ikinci bir istekle yüklenir (alt görevin id'si gerekir) → gövdeden AYRI parametre. */
  onSave: (body: SubActionCreateRequest, files: File[]) => void
  onHide: () => void
}

/**
 * Alt Görev Oluştur popup'ı (analiz 7.16.1): Tanım + Açıklama + Atanan + Hedef Teslim Tarihi
 * + "ana görevi görebilsin" onay kutusu + <b>Dokümanlar</b>. Kaydet → zorunlu alanlar doğrulanır
 * (açıklama, onay kutusu ve dokümanlar zorunlu değildir).
 *
 * <p><b>Dokümanlar burada yalnız TOPLANIR, yüklenmez</b> (kullanıcı isteği 28.09.2026): dosya ancak
 * kayıt id aldıktan sonra iliştirilebiliyordu, yani Alt Görev Detayı'na girmek gerekiyordu. Seçilen
 * dosyalar {@code onSave}'in ikinci parametresiyle çağırana verilir; yükleme
 * {@code useCreateSubAction} içinde yapılır. Ana görevdeki {@code StandaloneActionFormDialog} ile
 * aynı kalıp.
 *
 * <p><b>Açıklama 2026-09-09'da eklendi</b> (kullanıcı isteği). Sınır 2000 karakter — ana görevle aynı
 * ({@code StandaloneActionCreateRequest.description}). Sınır <b>karakter</b> cinsindendir; kolonun
 * <b>bayt</b> genişliği ayrı bir kısıttır — {@code MEETING_MINUTES.DESCRIPTION} 2026-09-15'ten beri
 * {@code VARCHAR2(4000)}'dir, çünkü 2000 karakterlik Türkçe metin 4000 bayt eder ve 2000 baytlık
 * kolona sığmıyordu. <b>Zorunlu DEĞİLDİR</b> (kullanıcı kararı
 * 2026-09-14) — ana görevden bilinçli ayrışır: alt görev çoğu zaman ana görevin bağlamını
 * devraldığı için ayrı bir kapsam metni yazdırmak boş yere sürtünme üretiyordu.
 *
 * <p><b>Onay kutusu 2026-09-10'da eklendi</b> (kullanıcı isteği): işaretlenirse alt görevin
 * sorumlusu ana görevi <b>salt-okuma</b> görebilir, işaretlenmezse göremez. Varsayılan
 * <b>2026-09-14'ten beri işaretlidir</b> (kullanıcı kararı): alt görev açan kişi çoğunlukla sorumlunun
 * bağlamı görmesini istiyordu ve her seferinde kutuyu elle işaretliyordu. DB'ye
 * {@code REFERANS(YES_NO)} kimliği yazılır (11/10); çeviriyi <b>sunucu</b> yapar, bu bileşen
 * boolean gönderir.
 *
 * <p><b>Sorumlu çoklu seçilir (kullanıcı isteği 20.10.2026):</b> seçilen <b>her kişi için ayrı bir alt
 * görev</b> açılır; başlık, açıklama, termin, onay kutusu ve dokümanlar hepsinde aynıdır. Her alt görevin
 * yine tek atananı vardır (066/AK-5) — çoklu seçim yalnız oluşturmayı topluca yapmanın yoludur. Toplu
 * yazımı sunucu tek transaction'da yapar ({@code ActionDetailService#createSubAction}); istemcide kişi
 * başına istek atılmaz, çünkü ara bir hata "bir kısmı oluştu" durumu bırakır ve yeniden Kaydet
 * oluşanları mükerrer açardı.
 */
export default function SubActionDialog({
  visible,
  minDeadline,
  maxDeadline,
  saving,
  onSave,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  // Seçim sırası korunur: alt görevler bu sırayla açılır ve numaralanır.
  const [assignees, setAssignees] = useState<MinutePerson[]>([])
  const [deadline, setDeadline] = useState('')
  // Varsayılan İŞARETLİ (kullanıcı kararı 2026-09-14): alt görevi açan kişi çoğunlukla sorumlunun
  // bağlamı görmesini istiyor; önceki İŞARETSİZ varsayılan her seferinde elle tıklamayı gerektiriyordu.
  // Kutu hâlâ kapatılabilir — kapatan taraf artık kullanıcının bilinçli eylemidir.
  const [canSeeMainTask, setCanSeeMainTask] = useState(true)
  // Oluşturma anında seçilen dokümanlar — sunucuya gitmez; alt görev kaydı id alınca çağıran yükler.
  const [files, setFiles] = useState<File[]>([])
  const [showErrors, setShowErrors] = useState(false)

  useEffect(() => {
    if (visible) {
      setName('')
      setDescription('')
      setAssignees([])
      setDeadline('')
      setCanSeeMainTask(true)
      // Dosyalar da sıfırlanmalı: kalırsa bir sonraki alt görev, öncekinin ekleriyle açılırdı.
      setFiles([])
      setShowErrors(false)
    }
  }, [visible])

  // Açıklama BURADA YOK: isteğe bağlıdır (kullanıcı kararı 2026-09-14). Sunucu da zorunlu tutmaz —
  // {@code SubActionCreateRequest.description} ve {@code ActionDetailService#createSubAction}.
  const invalid = {
    name: name.trim() === '',
    assignees: assignees.length === 0,
    deadline: deadline === '',
  }
  const hasError = invalid.name || invalid.assignees || invalid.deadline

  const submit = () => {
    if (hasError) {
      setShowErrors(true)
      return
    }
    onSave(
      {
        name: name.trim(),
        description: description.trim(),
        deadline,
        assignees: assignees.map(minutePersonToInput),
        canSeeMainTask,
      },
      files,
    )
  }

  // Seçici aynı sicili ikinci kez eklemez (ActionPeopleEditor); burada yine de korunur ki aynı kişiye
  // iki alt görev açılmasın — sunucu da tekilleştirir.
  const addAssignee = (p: PersonInput) =>
    setAssignees((prev) =>
      prev.some((x) => x.employeeId === p.employeeId) ? prev : [...prev, personInputToMinutePerson(p)],
    )
  const removeAssignee = (employeeId: string) =>
    setAssignees((prev) => prev.filter((x) => x.employeeId !== employeeId))

  const footer = (
    <div className="ui-dialog-footer">
      <Button icon="pi pi-check" label={t('actions.detail.subActions.save')} onClick={submit} loading={saving} />
    </div>
  )

  return (
    <Dialog
      visible={visible}
      header={t('actions.detail.subActions.addTitle')}
      footer={footer}
      onHide={onHide}
      modal
      dismissableMask
      draggable={false}
      style={{ width: '38rem', maxWidth: '95vw' }}
    >
      <div className="act-form">
        <label className="act-field act-field--full">
          <span className="ui-field-label">
            {t('actions.detail.subActions.name')} <span className="ui-req">*</span>
          </span>
          <InputTextarea
            value={name}
            rows={3}
            autoResize
            maxLength={250}
            className={showErrors && invalid.name ? 'p-invalid' : undefined}
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <label className="act-field act-field--full">
          <span className="ui-field-label">
            {t('actions.detail.subActions.description')}
          </span>
          <InputTextarea
            value={description}
            rows={4}
            autoResize
            maxLength={2000}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        {/* Sorumlu — çoklu seçim, en az bir kişi zorunlu (20.10.2026). Her kişi için ayrı alt görev
            açılır; alt görevin kendisi yine tek atananlıdır (066/AK-5). */}
        <div className="act-field act-field--full">
          <span className="ui-field-label">
            {t('actions.form.assignee')} <span className="ui-req">*</span>
          </span>
          <ActionPeopleEditor
            people={assignees}
            editable
            onAdd={addAssignee}
            onRemove={removeAssignee}
            placeholder={t('actions.detail.assignees.placeholder')}
            removeLabel={t('actions.detail.assignees.remove')}
          />
          {assignees.length > 1 && (
            <span className="act-empty-hint">
              {t('actions.detail.subActions.perAssigneeHint', { count: assignees.length })}
            </span>
          )}
          {showErrors && invalid.assignees && (
            <span className="act-form-err">{t('actions.create.required')}</span>
          )}
        </div>

        <label className="act-field act-field--full">
          <span className="ui-field-label">
            {t('actions.form.deadline')} <span className="ui-req">*</span>
          </span>
          <Calendar
            value={parseISODate(deadline)}
            minDate={minDeadline}
            maxDate={maxDeadline}
            dateFormat="dd.mm.yy"
            showIcon
            className={showErrors && invalid.deadline ? 'p-invalid' : undefined}
            onChange={(e) => setDeadline(toISODate(e.value ?? null))}
          />
        </label>

        {/*
          "Atanan kişi ana görevi de görebilsin" (kullanıcı kuralı 2026-09-10). İşaretlenirse alt
          görevin sorumlusu Alt Görev Detay'da "Ana Görevi Gör" butonunu görür ve ana görevi
          SALT-OKUMA açar. İşaretlenmezse ana görev de, kardeş alt görevler de, görev toplantıdan
          geliyorsa tutanak da ona kapalıdır.

          Zorunlu değildir ve <b>varsayılan olarak İŞARETLİDİR</b> (kullanıcı kararı 2026-09-14;
          2026-09-10'da işaretsiz gelmişti) — kutuyu kapatmak artık oluşturanın bilinçli eylemidir.

          DİKKAT: bu yalnız bir "buton çizilsin mi" bayrağı DEĞİLDİR — değer sunucuda gerçek okuma
          kapsımı sorgusunda kullanılıyor (ActionViewScope → SUB_SEES_PARENT_R). Varsayılanın tikli
          olması, sorumlunun — üst görevin toplantısına hiç dahil olmasa bile — ana görevi ve
          tutanağı görebilmesi demektir.
        */}
        <label className="act-field act-field--full act-field--check">
          <Checkbox
            inputId="sub-can-see-main"
            checked={canSeeMainTask}
            onChange={(e) => setCanSeeMainTask(!!e.checked)}
          />
          <span>{t('actions.detail.subActions.canSeeMainTask')}</span>
        </label>

        {/* Dokümanlar — OPSİYONEL. Ana görevdeki alanla aynı metinler ve aynı kabul listesi; alt
            görev de bir MEETING_MINUTES kaydı olduğu için ekler aynı uca gider. */}
        <div className="act-field act-field--full">
          <span className="ui-field-label">{t('actions.detail.documents.title')}</span>
          <FileUpload
            files={files}
            onChange={setFiles}
            accept={DOCUMENT_ACCEPT}
            label={t('actions.detail.documents.add')}
            hint={t('actions.detail.documents.hint')}
          />
        </div>
      </div>
    </Dialog>
  )
}
